import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { whatsappNumbers } from '../../data/contact';

const Wrap = styled.div`
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;

  @media (max-width: 768px) {
    bottom: 20px;
    right: 16px;
  }
`;

const Panel = styled(motion.div)`
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8eef3;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.15);
  overflow: hidden;
  min-width: 240px;

  .head {
    padding: 14px 16px;
    background: linear-gradient(135deg, #0e6b3c, #15803d);
    color: #fff;

    strong {
      display: block;
      font-family: var(--font-display);
      font-size: 0.9rem;
      font-weight: 700;
    }

    span {
      font-size: 0.75rem;
      opacity: 0.9;
    }
  }
`;

const Option = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  text-decoration: none;
  color: #0f172a;
  border-top: 1px solid #f1f5f9;
  transition: background 0.2s;

  &:hover {
    background: #f0faf4;
  }

  .flag {
    font-size: 1.25rem;
    line-height: 1;
  }

  .info {
    flex: 1;

    strong {
      display: block;
      font-family: var(--font-display);
      font-size: 0.85rem;
      font-weight: 700;
      color: #0f172a;
    }

    span {
      font-size: 0.8rem;
      color: #64748b;
    }
  }

  i.wa {
    color: #25d366;
    font-size: 1.1rem;
  }
`;

const MainBtn = styled(motion.button)`
  width: 58px;
  height: 58px;
  border-radius: 50%;
  border: none;
  background: #25d366;
  color: #fff;
  font-size: 1.6rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 28px rgba(37, 211, 102, 0.45);

  @media (max-width: 768px) {
    width: 54px;
    height: 54px;
    font-size: 1.45rem;
  }
`;

const WhatsAppFloat = () => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <Wrap ref={wrapRef}>
      <AnimatePresence>
        {open && (
          <Panel
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div className="head">
              <strong>Chat on WhatsApp</strong>
              <span>Choose your region</span>
            </div>
            {whatsappNumbers.map((n) => (
              <Option
                key={n.id}
                href={n.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
              >
                <span className="flag" aria-hidden="true">
                  {n.flag}
                </span>
                <div className="info">
                  <strong>{n.label}</strong>
                  <span>{n.display}</span>
                </div>
                <i className="fab fa-whatsapp wa" aria-hidden="true" />
              </Option>
            ))}
          </Panel>
        )}
      </AnimatePresence>

      <MainBtn
        type="button"
        aria-label={open ? 'Close WhatsApp menu' : 'Open WhatsApp menu'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, type: 'spring', stiffness: 260 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
      >
        <i className={open ? 'fas fa-times' : 'fab fa-whatsapp'} />
      </MainBtn>
    </Wrap>
  );
};

export default WhatsAppFloat;
